import React, { useState, useEffect } from "react";
import dayjs, { Dayjs } from "dayjs";
import { Calendar } from "lucide-react";
import TimePicker from "react-time-picker";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";

// Locale-aware date/time formatting
const formatDateTimeLocale = (date: Date | Dayjs | null | undefined, showTime: boolean): string => {
  if (!date) return "";
  const d = date instanceof Date ? date : date.toDate();
  if (showTime) {
    return d.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

// Detect if locale uses 12-hour format (has AM/PM)
const uses12HourFormat = (() => {
  const formatted = new Date().toLocaleTimeString([], { hour: 'numeric' });
  return /AM|PM/i.test(formatted);
})();

// Time format based on locale
const timeFormat = uses12HourFormat ? "h:mm a" : "HH:mm";

interface DateTimePickerProps {
  value?: Dayjs | null;
  onChange?: (value: Dayjs | null) => void;
  className?: string;
  disabled?: boolean;
  showTime?: boolean;
}

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  onChange,
  className,
  disabled = false,
  showTime = true,
}) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<string>("");
  const [time, setTime] = useState<string | null>(null);

  // Sync internal state with value prop
  useEffect(() => {
    if (value) {
      setDate(value.format("YYYY-MM-DD"));
      setTime(value.format("HH:mm"));
    }
  }, [value]);

  const formatDisplay = (d: Dayjs | null | undefined): string => {
    return formatDateTimeLocale(d, showTime);
  };

  const handleApply = () => {
    if (date) {
      const datetime = showTime && time
        ? dayjs(`${date} ${time}`)
        : dayjs(date);
      onChange?.(datetime);
    }
    setOpen(false);
  };

  const displayValue = value
    ? formatDisplay(value)
    : "Select date";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "justify-start text-left font-normal h-9",
            !value && "text-muted-foreground",
            className
          )}
        >
          <Calendar className="mr-2 h-4 w-4" />
          <span className="text-sm">{displayValue}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4" align="start">
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </div>

          {showTime && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Time</label>
              <div className="time-input-wrapper">
                <TimePicker
                  value={time}
                  onChange={(val) => setTime(val as string | null)}
                  format={timeFormat}
                  disableClock
                  clearIcon={null}
                  clockIcon={null}
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="link" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="segment" onClick={handleApply}>
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
